import {
  deselectAccidentDeathCapitalProduct,
  deselectAccidentDisabilityCapitalProduct,
  deselectAccidentMedicalExpensesSupplementProduct,
  deselectBasicProduct,
  deselectComplementaryProduct,
  deselectDailyCashProduct,
  deselectDentalProduct,
  deselectEmergencyCareProduct,
  deselectHospitalCapitalProduct,
  deselectHospitalDailyCashProduct,
  deselectHospitalProduct,
  deselectLegalProtectionProduct,
  deselectOutpatientProduct,
  deselectSicknessDeathOrDisabilityCapitalProduct,
  selectAccidentDeathCapitalProduct,
  selectAccidentDisabilityCapitalProduct,
  selectAccidentMedicalExpensesSupplementProduct,
  selectBasicProduct,
  selectComplementaryProduct,
  selectDailyCashProduct,
  selectDentalProduct,
  selectEmergencyCareProduct,
  selectHospitalCapitalProduct,
  selectHospitalDailyCashProduct,
  selectHospitalProduct,
  selectLegalProtectionProduct,
  selectOutpatientProduct,
  selectSicknessDeathOrDisabilityCapitalProduct,
} from './actions';
import { ActionCreator, on, ReducerTypes } from '@ngrx/store';
import { OfferState, PersonSupplementaryInsuranceState, SupplementaryInsuranceDiscounts } from '../offer.state';
import { applyToPersonWithId } from '../offer.reducer.util';
import { SupplementaryInsurancePrices } from '../../../api';

function changeSupplementaryInsurance(
  offerState: OfferState,
  personId: number,
  reducer: (data: PersonSupplementaryInsuranceState) => PersonSupplementaryInsuranceState,
): OfferState {
  return {
    ...offerState,
    persons: applyToPersonWithId(personId, offerState.persons, (person) => ({
      ...person,
      supplementaryInsurances: reducer(person.supplementaryInsurances),
      totalPrice: undefined,
    })),
    totalPrice: undefined,
  };
}

export const mapSupplementaryInsuranceDiscounts = (
  supplementaryInsurance: PersonSupplementaryInsuranceState,
  prices: SupplementaryInsurancePrices,
): SupplementaryInsuranceDiscounts => {
  return {
    basic: supplementaryInsurance.basic ? prices.basic.discounts : undefined,
    hospital: supplementaryInsurance.hospital ? prices.hospital.discounts : undefined,
    complementary: supplementaryInsurance.complementary ? prices.complementary.discounts : undefined,
    outpatient: supplementaryInsurance.outpatient ? prices.outpatient.discounts : undefined,
    legalProtection: supplementaryInsurance.legalProtection ? prices.legal_protection.discounts : undefined,
    accidentDisabilityCapital: supplementaryInsurance.accidentDisabilityCapital
      ? prices.accident_disability_capital.discounts
      : undefined,
    dailyCash: supplementaryInsurance.dailyCash ? prices.daily_cash.discounts : undefined,
    accidentDeathCapital: supplementaryInsurance.accidentDeathCapital
      ? prices.accident_death_capital.discounts
      : undefined,
    sicknessDeathOrDisabilityCapital: supplementaryInsurance.sicknessDeathOrDisabilityCapital
      ? prices.sickness_death_or_disability_capital.discounts
      : undefined,
    dental: supplementaryInsurance.dental ? prices.dental.discounts : undefined,
    hospitalCapital: supplementaryInsurance.hospitalCapital ? prices.hospital_capital.discounts : undefined,
    hospitalDailyCash: supplementaryInsurance.hospitalDailyCash ? prices.hospital_daily_cash.discounts : undefined,
    emergencyCare: supplementaryInsurance.emergencyCare ? prices.emergency_care.discounts : undefined,
    accidentMedicalExpensesSupplement: supplementaryInsurance.accidentMedicalExpensesSupplement
      ? prices.accident_medical_expenses_supplement.discounts
      : undefined,
  };
};

export const mapPricesToProducts = (
  supplementaryInsurance: PersonSupplementaryInsuranceState,
  prices: SupplementaryInsurancePrices,
): PersonSupplementaryInsuranceState => {
  return {
    basic: supplementaryInsurance.basic
      ? {
          ...supplementaryInsurance.basic,
          price: prices.basic.price,
        }
      : undefined,
    hospital: supplementaryInsurance.hospital
      ? {
          ...supplementaryInsurance.hospital,
          price: prices.hospital.price,
        }
      : undefined,
    complementary: supplementaryInsurance.complementary
      ? {
          ...supplementaryInsurance.complementary,
          price: prices.complementary.price,
        }
      : undefined,
    outpatient: supplementaryInsurance.outpatient
      ? {
          ...supplementaryInsurance.outpatient,
          price: prices.outpatient.price,
        }
      : undefined,
    legalProtection: supplementaryInsurance.legalProtection
      ? {
          ...supplementaryInsurance.legalProtection,
          price: prices.legal_protection.price,
        }
      : undefined,

    accidentDisabilityCapital: supplementaryInsurance.accidentDisabilityCapital
      ? {
          ...supplementaryInsurance.accidentDisabilityCapital,
          price: prices.accident_disability_capital.price,
        }
      : undefined,
    dailyCash: supplementaryInsurance.dailyCash
      ? {
          ...supplementaryInsurance.dailyCash,
          price: prices.daily_cash.price,
        }
      : undefined,
    accidentDeathCapital: supplementaryInsurance.accidentDeathCapital
      ? {
          ...supplementaryInsurance.accidentDeathCapital,
          price: prices.accident_death_capital.price,
        }
      : undefined,
    sicknessDeathOrDisabilityCapital: supplementaryInsurance.sicknessDeathOrDisabilityCapital
      ? {
          ...supplementaryInsurance.sicknessDeathOrDisabilityCapital,
          price: prices.sickness_death_or_disability_capital.price,
        }
      : undefined,
    dental: supplementaryInsurance.dental
      ? {
          ...supplementaryInsurance.dental,
          price: prices.dental.price,
        }
      : undefined,
    hospitalCapital: supplementaryInsurance.hospitalCapital
      ? {
          ...supplementaryInsurance.hospitalCapital,
          price: prices.hospital_capital.price,
        }
      : undefined,
    hospitalDailyCash: supplementaryInsurance.hospitalDailyCash
      ? {
          ...supplementaryInsurance.hospitalDailyCash,
          price: prices.hospital_daily_cash.price,
        }
      : undefined,

    emergencyCare: supplementaryInsurance.emergencyCare
      ? {
          ...supplementaryInsurance.emergencyCare,
          price: prices.emergency_care.price,
        }
      : undefined,
    accidentMedicalExpensesSupplement: supplementaryInsurance.accidentMedicalExpensesSupplement
      ? {
          ...supplementaryInsurance.accidentMedicalExpensesSupplement,
          price: prices.accident_medical_expenses_supplement.price,
        }
      : undefined,
  };
};

export const supplementaryInsuranceReducers: ReducerTypes<OfferState, ActionCreator[]>[] = [
  on(selectOutpatientProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      outpatient: {
        level: payload.level,
        price: undefined,
      },
    })),
  ),
  on(deselectOutpatientProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      outpatient: undefined,
    })),
  ),
  on(selectComplementaryProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      complementary: {
        level: payload.level,
        price: undefined,
      },
    })),
  ),
  on(deselectComplementaryProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      complementary: undefined,
    })),
  ),
  on(selectBasicProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      basic: {
        offerType: payload.offerType,
        costSharing: payload.costSharing,
        region: payload.region,
        price: undefined,
      },
    })),
  ),
  on(deselectBasicProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      basic: undefined,
    })),
  ),
  on(selectHospitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      hospital: {
        offerType: payload.offerType,
        costSharing: payload.costSharing,
        region: payload.region,
        price: undefined,
      },
    })),
  ),
  on(deselectHospitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      hospital: undefined,
    })),
  ),
  on(selectHospitalCapitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      hospitalCapital: {
        capital: payload.capital,
        accidentCoverage: payload.accidentCoverage,
        price: undefined,
      },
    })),
  ),
  on(deselectHospitalCapitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      hospitalCapital: undefined,
    })),
  ),
  on(selectAccidentDeathCapitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      accidentDeathCapital: {
        accidentBenefit: payload.accidentBenefit,
        price: undefined,
      },
    })),
  ),
  on(deselectAccidentDeathCapitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      accidentDeathCapital: undefined,
    })),
  ),
  on(selectAccidentDisabilityCapitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      accidentDisabilityCapital: {
        accidentBenefit: payload.accidentBenefit,
        price: undefined,
      },
    })),
  ),
  on(deselectAccidentDisabilityCapitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      accidentDisabilityCapital: undefined,
    })),
  ),
  on(selectSicknessDeathOrDisabilityCapitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      sicknessDeathOrDisabilityCapital: {
        deathBenefit: payload.deathBenefit,
        disabilityBenefit: payload.disabilityBenefit,
        price: undefined,
      },
    })),
  ),
  on(deselectSicknessDeathOrDisabilityCapitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      sicknessDeathOrDisabilityCapital: undefined,
    })),
  ),
  on(selectLegalProtectionProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      legalProtection: {
        price: undefined,
      },
    })),
  ),
  on(deselectLegalProtectionProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      legalProtection: undefined,
    })),
  ),
  on(selectDentalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      dental: {
        level: payload.level,
        price: undefined,
      },
    })),
  ),
  on(deselectDentalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      dental: undefined,
    })),
  ),
  on(selectHospitalDailyCashProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      hospitalDailyCash: {
        benefitDuration: payload.benefitDuration,
        capital: payload.capital,
        waitingPeriod: payload.waitingPeriod,
        price: undefined,
      },
    })),
  ),
  on(deselectHospitalDailyCashProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      hospitalDailyCash: undefined,
    })),
  ),
  on(selectDailyCashProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      dailyCash: {
        accidentCoverage: payload.accidentCoverage,
        capital: payload.capital,
        waitingPeriod: payload.waitingPeriod,
        price: undefined,
      },
    })),
  ),
  on(deselectDailyCashProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      dailyCash: undefined,
    })),
  ),
  on(selectEmergencyCareProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      emergencyCare: {
        price: undefined,
      },
    })),
  ),
  on(deselectEmergencyCareProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      emergencyCare: undefined,
    })),
  ),
  on(selectAccidentMedicalExpensesSupplementProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      accidentMedicalExpensesSupplement: {
        price: undefined,
      },
    })),
  ),
  on(deselectAccidentMedicalExpensesSupplementProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      accidentMedicalExpensesSupplement: undefined,
    })),
  ),
];
